/**
 * 관리자 화면 ↔ 주소.
 *
 * 화면은 여전히 App 의 상태(menu · selectedOrder 등 · creatingOrder)가 고르고, 주소는 그 상태를
 * 비추는 거울이다(@solar/ui router.ts). 여기엔 양방향 변환만 둔다 — 새 메뉴는 MenuKey 와
 * MENU_PATH 에 한 줄씩 추가한다. 경로 이름은 레거시 Laravel 라우트(/web/order, /web/as …)
 * 가 아니라 메뉴 구조를 따른다.
 */

export type MenuKey =
  | 'dashboard'
  | 'orders'
  | 'uninstall'
  | 'as'
  | 'subsidy_si'
  | 'subsidy_gu'
  | 'capacity'
  | 'terminal'
  | 'product'
  | 'inventory_list'
  | 'inventory_edit'
  | 'company'
  | 'team'
  | 'board_notice'
  | 'board_data'
  | 'board_member'
  | 'board_citizen'
  | 'board_faq'
  | 'stat_order'
  | 'stat_money'
  | 'stat_as'
  | 'pdf_2018'
  | 'permission'
  | 'role'
  | 'user'
  | 'user_history'
  | 'pw_change_users'
  | 'role_history';

export const MENU_PATH: Record<MenuKey, string> = {
  dashboard: '/',
  orders: '/orders',
  uninstall: '/uninstall',
  as: '/as',
  subsidy_si: '/subsidy/si',
  subsidy_gu: '/subsidy/gu',
  capacity: '/capacity',
  terminal: '/terminal',
  product: '/product',
  inventory_list: '/inventory',
  inventory_edit: '/inventory/edit',
  company: '/company',
  team: '/team',
  board_notice: '/board/notice',
  board_data: '/board/data',
  board_member: '/board/member',
  board_citizen: '/board/citizen',
  board_faq: '/board/faq',
  stat_order: '/stat/order',
  stat_money: '/stat/money',
  stat_as: '/stat/as',
  pdf_2018: '/pdf-2018',
  permission: '/permission',
  role: '/role',
  user: '/user',
  user_history: '/user/history',
  pw_change_users: '/user/pw-change',
  role_history: '/role/history',
};

const PATH_MENU = new Map<string, MenuKey>(
  (Object.entries(MENU_PATH) as Array<[MenuKey, string]>).map(([k, p]) => [p, k]),
);

export interface AdminRoute {
  menu: MenuKey;
  creatingOrder: boolean;
  selectedOrder: number | null;
  selectedUninstall: number | null;
  selectedAs: number | null;
}

export const HOME: AdminRoute = {
  menu: 'dashboard',
  creatingOrder: false,
  selectedOrder: null,
  selectedUninstall: null,
  selectedAs: null,
};

export function routeToPath(r: AdminRoute): string {
  if (r.menu === 'orders') {
    if (r.creatingOrder) return '/orders/new';
    return r.selectedOrder === null ? '/orders' : `/orders/${r.selectedOrder}`;
  }
  if (r.menu === 'uninstall' && r.selectedUninstall !== null) return `/uninstall/${r.selectedUninstall}`;
  if (r.menu === 'as' && r.selectedAs !== null) return `/as/${r.selectedAs}`;
  return MENU_PATH[r.menu];
}

/** 모르는 경로는 대시보드. 끝의 `/` 는 무시한다. */
export function pathToRoute(path: string): AdminRoute {
  const clean = path.length > 1 ? path.replace(/\/+$/, '') : path;
  const exact = PATH_MENU.get(clean);
  if (exact) return { ...HOME, menu: exact };

  if (clean === '/orders/new') return { ...HOME, menu: 'orders', creatingOrder: true };
  const detail = /^\/(orders|uninstall|as)\/(\d+)$/.exec(clean);
  if (detail) {
    const id = Number(detail[2]);
    if (detail[1] === 'orders') return { ...HOME, menu: 'orders', selectedOrder: id };
    if (detail[1] === 'uninstall') return { ...HOME, menu: 'uninstall', selectedUninstall: id };
    return { ...HOME, menu: 'as', selectedAs: id };
  }
  return HOME;
}
